import { Link, NavLink } from "@remix-run/react";
import { Suspense } from "react";
import { User } from "~/services/auth.server";
import { Notification } from "~/types";
import CellShadeWrapper from "./CellShadeWrapper";
import DefaultProfilePic from "./DefaultProfilePic";
import GremlinIcon from "./GremlinIcon";
import NavBarUserIcon from "./NavBarUserIcon";
import SearchModal from "./SearchModal";

type NavBarProps = {
  user?: User | null;
  notifications?: Notification[];
};

export default function NavBar({ user }: NavBarProps) {
  return (
    <nav
      className="fixed inset-x-0 w-screen bottom-0 z-20 flex h-max flex-row items-center gap-x-2
                  py-2 text-white drop-shadow-sm lg:bottom-full lg:top-0
                  lg:border-b-2 lg:border-black px-2 bg-[#553075]"
    >
      <div className="text-sm lg:text-base flex w-full items-center justify-around gap-x-4 lg-gap-x-2 font-navbar-menu lg:w-auto lg:justify-normal">
        <div className="flex flex-col items-center">
          <Suspense
            fallback={
              <span className="animate-pulse ml-2 lg:ml-12 w-6 lg:w-12 h-6 lg:h-12">
                <DefaultProfilePic size="24" fill="#fefefe" />
              </span>
            }
          >
            <NavBarUserIcon user={user} />
            <p className="block lg:hidden">Profile</p>
          </Suspense>
        </div>
        <div className="mx-2 hidden h-8 w-[1px] shrink-0 bg-white/30 lg:block 2xl:mx-6"></div>
        <NavLink
          prefetch="intent"
          to="/"
          className={({ isActive }) =>
            isActive ? "text-sm lg:text-lg font-bold" : "font-light"
          }
        >
          {({ isActive }) => (
            <div className="flex flex-col items-center">
              {isActive ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="#fefefe"
                  className="block lg:hidden"
                >
                  <path d="M21 13v10h-6v-6h-6v6h-6v-10h-3l12-12 12 12h-3zm-1-5.907v-5.093h-3v2.093l3 3z" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  className="block lg:hidden"
                >
                  <path
                    fill="#fefefe"
                    d="M20 7.093v-5.093h-3v2.093l3 3zm4 5.907l-12-12-12 12h3v10h7v-5h4v5h7v-10h3zm-5 8h-3v-5h-8v5h-3v-10.26l7-6.912 7 6.99v10.182z"
                  />
                </svg>
              )}
              <p>Home</p>
            </div>
          )}
        </NavLink>
        <NavLink
          prefetch="intent"
          to="/gremlins"
          className={({ isActive }) =>
            isActive ? "text-sm lg:text-lg font-bold" : "font-light"
          }
        >
          {({ isActive }) => (
            <div className="flex flex-col items-center">
              <span className="block lg:hidden">
                <GremlinIcon isActive={isActive} />
              </span>
              <p>Grems</p>
            </div>
          )}
        </NavLink>
        <NavLink
          prefetch="intent"
          to="/trades"
          className={({ isActive }) =>
            isActive ? "text-sm lg:text-lg font-bold" : "font-light"
          }
        >
          {({ isActive }) => (
            <div className="flex flex-col items-center">
              {isActive ? (
                <svg
                  width="24"
                  height="24"
                  xmlns="http://www.w3.org/2000/svg"
                  fillRule="evenodd"
                  clipRule="evenodd"
                  fill="#fefefe"
                  className="block lg:hidden"
                >
                  <path d="M12 0c6.623 0 12 5.377 12 12s-5.377 12-12 12-12-5.377-12-12 5.377-12 12-12zm2.085 14h-9v2h9v3l5-4-5-4v3zm-4-6v-3l-5 4 5 4v-3h9v-2h-9z" />
                </svg>
              ) : (
                <svg
                  width="24"
                  height="24"
                  xmlns="http://www.w3.org/2000/svg"
                  fillRule="evenodd"
                  clipRule="evenodd"
                  className="block lg:hidden"
                >
                  <path
                    fill="#fefefe"
                    d="M12 0c6.623 0 12 5.377 12 12s-5.377 12-12 12-12-5.377-12-12 5.377-12 12-12zm0 2c5.519 0 10 4.481 10 10s-4.481 10-10 10-10-4.481-10-10 4.481-10 10-10zm2 12v-3l5 4-5 4v-3h-9v-2h9zm-4-6v-3l-5 4 5 4v-3h9v-2h-9z"
                  />
                </svg>
              )}
              <p>Trades</p>
            </div>
          )}
        </NavLink>
        <span className="block lg:hidden">
          <SearchModal />
        </span>
        <NavLink
          prefetch="intent"
          to="/marketplace"
          className={({ isActive }) =>
            isActive ? "text-sm lg:text-lg font-bold" : "font-light"
          }
        >
          {({ isActive }) => (
            <div className="flex flex-col items-center">
              {isActive ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-6 h-6 block lg:hidden"
                >
                  <path d="M5.223 2.25c-.497 0-.974.198-1.325.55l-1.3 1.298A3.75 3.75 0 0 0 7.5 9.75c.627.47 1.406.75 2.25.75.844 0 1.624-.28 2.25-.75.626.47 1.406.75 2.25.75.844 0 1.623-.28 2.25-.75a3.75 3.75 0 0 0 4.902-5.652l-1.3-1.299a1.875 1.875 0 0 0-1.325-.549H5.223Z" />
                  <path
                    fillRule="evenodd"
                    d="M3 20.25v-8.755c1.42.674 3.08.673 4.5 0A5.234 5.234 0 0 0 9.75 12c.804 0 1.568-.182 2.25-.506a5.234 5.234 0 0 0 2.25.506c.804 0 1.567-.182 2.25-.506 1.42.674 3.08.675 4.5.001v8.755h.75a.75.75 0 0 1 0 1.5H2.25a.75.75 0 0 1 0-1.5H3Zm3-6a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-.75.75h-3a.75.75 0 0 1-.75-.75v-3Zm8.25-.75a.75.75 0 0 0-.75.75v5.25c0 .414.336.75.75.75h3a.75.75 0 0 0 .75-.75v-5.25a.75.75 0 0 0-.75-.75h-3Z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 block lg:hidden"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z"
                  />
                </svg>
              )}
              <p>Market</p>
            </div>
          )}
        </NavLink>
        <NavLink
          prefetch="intent"
          to="/notifications"
          className={({ isActive }) =>
            `hidden lg:block ${isActive ? "text-sm lg:text-lg font-bold" : "font-light"}`
          }
        >
          {({ isActive }) => (
            <div className="flex flex-col items-center">
              {isActive ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-6 h-6 block lg:hidden"
                >
                  <path d="M5.223 2.25c-.497 0-.974.198-1.325.55l-1.3 1.298A3.75 3.75 0 0 0 7.5 9.75c.627.47 1.406.75 2.25.75.844 0 1.624-.28 2.25-.75.626.47 1.406.75 2.25.75.844 0 1.623-.28 2.25-.75a3.75 3.75 0 0 0 4.902-5.652l-1.3-1.299a1.875 1.875 0 0 0-1.325-.549H5.223Z" />
                  <path
                    fillRule="evenodd"
                    d="M3 20.25v-8.755c1.42.674 3.08.673 4.5 0A5.234 5.234 0 0 0 9.75 12c.804 0 1.568-.182 2.25-.506a5.234 5.234 0 0 0 2.25.506c.804 0 1.567-.182 2.25-.506 1.42.674 3.08.675 4.5.001v8.755h.75a.75.75 0 0 1 0 1.5H2.25a.75.75 0 0 1 0-1.5H3Zm3-6a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-.75.75h-3a.75.75 0 0 1-.75-.75v-3Zm8.25-.75a.75.75 0 0 0-.75.75v5.25c0 .414.336.75.75.75h3a.75.75 0 0 0 .75-.75v-5.25a.75.75 0 0 0-.75-.75h-3Z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 block lg:hidden"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z"
                  />
                </svg>
              )}
              <p>Notifications</p>
            </div>
          )}
        </NavLink>
      </div>
      <span className="grow hidden lg:flex w-max items-center lg:ml-16 xl:ml-48">
        <SearchModal />
      </span>
      <div className="mr-4 hidden flex-row-reverse font-navbar-menu lg:flex">
        {!user ? (
          <Link to="/login" prefetch="intent" className="font-medium text-black">
            <CellShadeWrapper
              outerClassName="rounded-md"
              innerClassName="px-4 py-1 bg-white rounded-md"
            >
              Login
            </CellShadeWrapper>
          </Link>
        ) : (
          <div className="flex gap-x-2">
            <Link to="/marketplace" prefetch="intent" className="text-black">
              <CellShadeWrapper
                outerClassName="rounded-md"
                innerClassName="px-4 py-1 bg-white rounded-md"
              >
                New trade
              </CellShadeWrapper>
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
}
